import { useEffect, useRef, useCallback } from "react";
import { trackJobImpression } from "./useAnalytics";

/**
 * Tracks job card impressions using IntersectionObserver.
 * Each slug is tracked at most once per mount. Cards must be
 * at least 50% visible for 500ms before counting.
 */
export function useJobImpressions() {
  const seenRef = useRef<Set<string>>(new Set());
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    const timers = new Map<Element, ReturnType<typeof setTimeout>>();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const el = entry.target as HTMLElement;
          const slug = el.dataset.impressionSlug;
          if (!slug) continue;

          if (entry.isIntersecting && !seenRef.current.has(slug)) {
            const timer = setTimeout(() => {
              if (!seenRef.current.has(slug)) {
                seenRef.current.add(slug);
                trackJobImpression(slug);
              }
              timers.delete(el);
            }, 500);
            timers.set(el, timer);
          } else if (!entry.isIntersecting) {
            const timer = timers.get(el);
            if (timer) {
              clearTimeout(timer);
              timers.delete(el);
            }
          }
        }
      },
      { threshold: 0.5 },
    );

    return () => {
      for (const timer of timers.values()) clearTimeout(timer);
      observerRef.current?.disconnect();
    };
  }, []);

  const observeRef = useCallback((el: HTMLElement | null) => {
    if (el && observerRef.current) {
      observerRef.current.observe(el);
    }
  }, []);

  return { observeRef };
}
